// spinner.ts

class SpinnerComponent {
    private static instance: SpinnerComponent | null = null;
    private spinnerElement: HTMLElement | null = null;
    private overlayElement: HTMLElement | null = null;
    private isAttachedToBody: boolean = false;
    private currentDialog: HTMLElement | null = null;
    private originalOverflow: string = '';

    private constructor() {}

    /**
     * Initialize the Spinner singleton
     */
    public static init(): SpinnerComponent {
        if (!SpinnerComponent.instance) {
            SpinnerComponent.instance = new SpinnerComponent();
        }
        return SpinnerComponent.instance;
    }

    /**
     * Open the spinner with a message.
     * Attaches to an open dialog if one exists, otherwise attaches to body.
     * Prevents scrolling when attached to body.
     */
    public open(message: string = 'Loading...', options?: { target?: 'auto' | 'body' }): void {
        // Close any existing spinner first
        this.close();

        const forceBody = options?.target === 'body';

        // Check if any dialog is open
        const openDialogs = forceBody
            ? []
            : document.querySelectorAll('dialog[open]');
        const openDialog = openDialogs[openDialogs.length - 1] ?? null;

        if (openDialog) {
            // Attach to dialog - cast to HTMLElement
            this.attachToDialog(openDialog as HTMLElement, message);
        } else {
            // Attach to body
            this.attachToBody(message);
        }
    }

    /**
     * Close and remove the spinner from wherever it's attached
     * Restores scrolling if it was disabled
     */
    public close(): void {
        // Restore scrolling if it was disabled
        if (this.originalOverflow) {
            document.body.style.overflow = this.originalOverflow;
            this.originalOverflow = '';
        }

        // Remove overlay if exists
        if (this.overlayElement && this.overlayElement.parentNode) {
            if (this.overlayElement instanceof HTMLDialogElement && this.overlayElement.open) {
                this.overlayElement.close();
            }
            this.overlayElement.parentNode.removeChild(this.overlayElement);
        }
        this.overlayElement = null;

        // Remove spinner
        if (this.spinnerElement && this.spinnerElement.parentNode) {
            this.spinnerElement.parentNode.removeChild(this.spinnerElement);
        }
        this.spinnerElement = null;
        this.isAttachedToBody = false;
        this.currentDialog = null;
    }

    /**
     * Update the spinner message
     */
    public setMessage(message: string): void {
        if (!this.spinnerElement) {
            console.warn('Spinner is not open. Call open() first.');
            return;
        }

        const textElement = this.spinnerElement.querySelector('.spinner-text');
        if (textElement) {
            textElement.textContent = message;
        }
    }

    /**
     * Check if spinner is currently open
     */
    public isOpen(): boolean {
        return this.spinnerElement !== null;
    }

    /**
     * Check if spinner is attached to body
     */
    public isOnBody(): boolean {
        return this.isAttachedToBody;
    }

    /**
     * Check if spinner is attached to a dialog
     */
    public isOnDialog(): boolean {
        return this.currentDialog !== null;
    }

    /**
     * Get the current dialog the spinner is attached to (if any)
     */
    public getCurrentDialog(): HTMLElement | null {
        return this.currentDialog;
    }

    private attachToDialog(dialog: HTMLElement, message: string): void {
        // Create overlay
        this.overlayElement = this.createOverlayElement();
        
        // Style overlay for dialog
        // Note: absolute (not fixed) positioned against the dialog itself.
        // A dialog centered via `transform: translate(-50%, -50%)` becomes
        // the containing block for `position: fixed` descendants, which
        // makes `top/left: 0` resolve against the dialog's own corner while
        // 100dvw/100dvh still spans the real viewport — shifting the overlay
        // (and its centered content) away from the dialog's actual center.
        this.overlayElement.style.position = 'absolute';
        this.overlayElement.style.inset = '0';
        this.overlayElement.style.width = '100%';
        this.overlayElement.style.height = '100%';
        this.overlayElement.style.display = 'flex';
        this.overlayElement.style.alignItems = 'center';
        this.overlayElement.style.justifyContent = 'center';
        this.overlayElement.style.background = 'rgb(9 9 9 / 27%)';
        this.overlayElement.style.borderRadius = 'inherit';
        this.overlayElement.style.zIndex = '1000';
        this.overlayElement.style.pointerEvents = 'auto';
        this.overlayElement.style.cursor = 'default';

        // Create spinner content
        const spinnerContent = this.createSpinnerContent(message);
        
        // Style spinner for dialog
        const spinnerCircle = spinnerContent.querySelector('.spinner-circle') as HTMLElement | null;
        if (spinnerCircle) {
            spinnerCircle.style.cssText = `
                width: 40px;
                height: 40px;
                border: 4px solid #f3f3f3;
                border-top: 4px solid rgba(255, 8, 40, 1);
                border-radius: 50%;
                margin: 0 auto 10px;
                animation: spinnerSpin 1s linear infinite;
            `;
        }

        const text = spinnerContent.querySelector('.spinner-text') as HTMLElement | null;
        if (text) {
            text.style.cssText = `
                font-family: Arial, sans-serif;
                font-size: 14px;
                color: #ffffff;
                text-align: center;
            `;
        }

        this.overlayElement.appendChild(spinnerContent);
        
        // Add to dialog
        dialog.appendChild(this.overlayElement);
        this.spinnerElement = this.overlayElement;
        this.currentDialog = dialog;
        this.isAttachedToBody = false;
        
        // Ensure spinner styles exist
        this.ensureStyles();
    }

    private attachToBody(message: string): void {
        // Disable scrolling on body
        this.originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const overlay = document.createElement('dialog') as HTMLDialogElement;
        overlay.className = 'spinner-overlay';
        overlay.style.boxSizing = 'border-box';
        this.overlayElement = overlay;

        // Style overlay for body - full viewport coverage
        this.overlayElement.style.position = 'fixed';
        this.overlayElement.style.inset = '0';
        this.overlayElement.style.margin = '0';
        this.overlayElement.style.padding = '0';
        this.overlayElement.style.border = 'none';
        this.overlayElement.style.maxWidth = '100vw';
        this.overlayElement.style.maxHeight = '100vh';
        this.overlayElement.style.width = '100vw';
        this.overlayElement.style.height = '100vh';
        this.overlayElement.style.display = 'flex';
        this.overlayElement.style.alignItems = 'center';
        this.overlayElement.style.justifyContent = 'center';
        this.overlayElement.style.background = 'rgba(0, 0, 0, 0.7)';
        this.overlayElement.style.pointerEvents = 'auto';
        this.overlayElement.style.cursor = 'default';
        this.overlayElement.style.overflow = 'hidden';

        // Create spinner content
        const spinnerContent = this.createSpinnerContent(message);
        
        // Style spinner for body
        const spinnerCircle = spinnerContent.querySelector('.spinner-circle') as HTMLElement | null;
        if (spinnerCircle) {
            spinnerCircle.style.cssText = `
                width: 50px;
                height: 50px;
                border: 4px solid rgba(255,255,255,0.3);
                border-top: 4px solid rgba(255, 8, 40, 1);
                border-radius: 50%;
                margin: 0 auto 10px;
                animation: spinnerSpin 1s linear infinite;
            `;
        }

        const text = spinnerContent.querySelector('.spinner-text') as HTMLElement | null;
        if (text) {
            text.style.cssText = `
                font-family: Arial, sans-serif;
                font-size: 16px;
                color: white;
                text-align: center;
            `;
        }

        this.overlayElement.appendChild(spinnerContent);

        // Add to body and open in the top layer, above any open modal dialog
        document.body.appendChild(overlay);
        overlay.showModal();
        this.spinnerElement = this.overlayElement;
        this.currentDialog = null;
        this.isAttachedToBody = true;

        // Ensure spinner styles exist
        this.ensureStyles();
    }

    private createOverlayElement(): HTMLElement {
        const overlay = document.createElement('div');
        overlay.className = 'spinner-overlay';
        overlay.style.boxSizing = 'border-box';
        return overlay;
    }

    private createSpinnerContent(message: string): HTMLElement {
        const content = document.createElement('div');
        content.className = 'spinner-content';
        content.style.boxSizing = 'border-box';
        
        content.innerHTML = `
            <div class="spinner-circle"></div>
            <div class="spinner-text">${this.escapeHtml(message)}</div>
        `;
        
        return content;
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    private ensureStyles(): void {
        // Check if styles already exist
        if (document.getElementById('spinner-styles')) {
            return;
        }

        const style = document.createElement('style');
        style.id = 'spinner-styles';
        style.textContent = `
            @keyframes spinnerSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            
            .spinner-overlay {
                box-sizing: border-box;
            }
            
            .spinner-overlay * {
                box-sizing: border-box;
            }
            
            .spinner-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
            }
            
            .spinner-circle {
                flex-shrink: 0;
            }
        `;
        
        document.head.appendChild(style);
    }
}

// Export a default instance for convenience
export const Spinner = SpinnerComponent
.init();